import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { Heading, Message, Link, Text } from '@afterpaytouch/core'
import { useTranslation, Trans } from 'next-i18next'
import React, { useEffect, useRef, useState } from 'react'
import { Head, ModuleContainer, ShippingAddressList } from '../../component'
import { Route, useSummaryRoute } from '../../config/router'
import { CheckoutPage, ShippingAddress as ShippingAddressModel, TrackingEvent } from '../../model'
import {
  setChosenAddress,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useInternationalShippingEnabled,
  useIsShippingAddressValid,
  useShippingAddress,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'

export const ShippingAddressPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { checkout } = useCheckoutState()
  const { headingSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isInternationalShippingEnabled = useInternationalShippingEnabled()
  const hasValidShippingAddress = useIsShippingAddressValid()

  const { chosenAddress } = useShippingAddress()
  const summaryRoute = useSummaryRoute()
  const pageTitle = `${t('shippingAddress:chooseShippingAddress')} | ${t('common:brandName')}`
  const currAddressRef = useRef<ShippingAddressModel>(chosenAddress)
  const [disableNewAddressAction, setDisableNewAddressAction] = useState(false)

  const handleNewAddressClick = (): void => {
    setDisableNewAddressAction(true)
    logEvent(TrackingEvent.PRESSED_ADD_NEW_SHIPPING_ADDRESS)
    router.push(Route.NEW_SHIPPING_ADDRESS)
  }

  const handleShippingAddressChange = (address: ShippingAddressModel | undefined): void => {
    setDisableNewAddressAction(true)
    logEvent(TrackingEvent.SELECTED_SHIPPING_ADDRESS, address)
    if (valueIsNotNullOrUndefined(address)) {
      currAddressRef.current = address
    }
    onSave()
  }

  const onSave = (): void => {
    const currAddress = currAddressRef.current

    logEvent(TrackingEvent.PRESSED_SHIPPING_ADDRESS_SAVE_BUTTON)
    dispatch(setChosenAddress(currAddress))
    setDisableNewAddressAction(false)
    router.push(summaryRoute)
  }

  useEffect(() => {
    router.prefetch(Route.NEW_SHIPPING_ADDRESS)
  }, [])

  const renderMessageContent = (): JSX.Element => {
    const addNewAddress = <Link size='XS' onClick={handleNewAddressClick} kind='Underline' />
    const messageHeading = t('shippingAddress:notSupportedShippingAddress:heading')
    const messageBody = t('shippingAddress:notSupportedShippingAddress:body', { merchantName: checkout?.merchant?.name })
    const messageDescription = (
      <Text size='XS'>
        <Trans components={{ addNewAddress }} i18nKey={messageBody} />
      </Text>
    )

    return <Message iconName='Globe' heading={messageHeading} description={messageDescription} kind='Basic' />
  }

  return (
    <>
      <Head title={pageTitle} />
      <Heading size={headingSize}>{t('shippingAddress:chooseShippingAddress')}</Heading>
      {isInternationalShippingEnabled && renderMessageContent()}
      <ModuleContainer showBorder={false} padding={{ vertical: 'None' }}>
        <ShippingAddressList
          onChange={(address) => handleShippingAddressChange(address)}
          defaultShippingAddress={hasValidShippingAddress ? chosenAddress : undefined}
          addNewAddressClick={handleNewAddressClick}
          addNewAddressClickDisabled={disableNewAddressAction}
        />
      </ModuleContainer>
      <TrackPageViewed />
    </>
  )
}
